import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/Button';
import { Waves, BarChart3, Bell, CheckCircle } from 'lucide-react';
import { markOnboardingCompleted } from './onboardingState';

const features = [
  {
    icon: Waves,
    title: 'Track Water Quality',
    description: 'Log daily readings with the 5-slot system used by top farms',
  },
  {
    icon: BarChart3,
    title: 'Monitor Performance',
    description: 'See FCR, survival rates, and biomass at a glance',
  },
  {
    icon: Bell,
    title: 'Get Alerts',
    description: 'Instant notifications when readings need attention',
  },
  {
    icon: CheckCircle,
    title: 'Works Offline',
    description: 'Lab Technicians can log data even without internet',
  },
];

interface WelcomeScreenProps {
  onComplete: () => void;
}

export function WelcomeScreen({ onComplete }: WelcomeScreenProps) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const handleComplete = () => {
    markOnboardingCompleted();
    onComplete();
    navigate({ to: '/' });
  };

  const handleSkip = () => {
    markOnboardingCompleted();
    onComplete();
    navigate({ to: '/' });
  };

  if (step === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700 p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl" role="img" aria-label="shrimp">
              🦐
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome to HiponHero
          </h1>
          <p className="text-gray-600 mb-8">
            The smart way to manage your shrimp farm
          </p>
          <div className="space-y-3">
            <Button onClick={() => setStep(1)} className="w-full">
              Get Started
            </Button>
            <button
              onClick={handleSkip}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Skip intro
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        <div className="text-center mb-8">
          <h2 className="text-xl font-bold text-gray-900">
            What HiponHero Does
          </h2>
        </div>

        <div className="space-y-4 mb-8">
          {features.map((feature, index) => (
            <div key={index} className="flex gap-4">
              <div className="p-2 bg-primary-100 rounded-lg h-fit shrink-0">
                <feature.icon className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{feature.title}</h3>
                <p className="text-sm text-gray-500">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        <Button onClick={handleComplete} className="w-full">
          Start Using HiponHero
        </Button>
      </div>
    </div>
  );
}
